import { Injectable, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { tap, timeout } from 'rxjs';
import { ApiService } from './api.service';
import { WIZTASKS_AUTH_TOKEN_KEY, decodeJwtPayload } from '../utils/jwt.util';

/** Grantable Operator Console permission keys (mirror of Operator_access::PERMISSIONS). */
export const OPERATOR_PERMISSION_KEYS = [
  'billing.manage',
  'plans.manage',
  'operators.manage',
  'impersonate',
  'workspaces.view',
] as const;

export interface User {
  id: number;
  email: string;
  full_name: string;
  avatar_url?: string | null;
  timezone?: string;
  role?: string;
  global_role?: 'SYSTEM_ADMIN' | 'STANDARD_USER' | 'GLOBAL_VIEWER';
  is_super_admin?: number | boolean;
  auth_token?: string;
  token_type?: 'Bearer';
  expires_in?: number;
  /** From global_permission_grants via user's groups (see api/sql/012, 013, 039). */
  global_permissions?: string[];
  /** From API: false for default viewers unless projects/add override. */
  can_create_project?: boolean;
  /** From API: Jira Administrator–style project delete/trash (system admin or projects/delete). */
  can_delete_project?: boolean;
  /** From API: false for default viewers unless issues/add override. */
  can_create_issues?: boolean;
  /** From API: true unless site role is viewer without issues add/edit/delete matrix unlock. */
  site_viewer_issues_unlocked?: boolean;
  /** 2FA method: 'totp' | 'email' or null if disabled. */
  two_factor_method?: 'totp' | 'email' | null;
  notification_retention_days?: number;
  /** Operator Console: ROOT operator (implicitly every operator permission). */
  is_operator_root?: number | boolean;
  /** Operator Console: per-operator permission keys. */
  operator_permissions?: string[];
  /** Operator was created with a temporary password — force a change on next login. */
  must_change_password?: number | boolean;
  /** Operator 2-step verification is required platform-wide but not yet enabled on this account. */
  operator_2fa_required?: boolean;
  /** Set while an operator is impersonating a workspace account for troubleshooting. */
  impersonating?: {
    operator_id: number;
    operator_email: string;
    operator_name: string;
    site_id: number;
    since: string;
  } | null;
  /** Workspace RBAC for the currently resolved /w/<slug> workspace. */
  workspace?: {
    site_id: number;
    role: 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER' | null;
    is_workspace_admin: boolean;
    is_operator: boolean;
    permissions: string[];
    allow_member_create_project: boolean;
  };
}

/** Response when login requires 2FA verification. */
export interface Login2FARequired {
  requires_two_factor: true;
  pending_token: string;
  method: 'totp' | 'email';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSignal = signal<User | null>(null);
  user = this.userSignal.asReadonly();
  isLoggedIn = computed(() => !!this.userSignal());
  /** WizCode operator (instance super-admin) — cross-workspace console only. */
  isOperator = computed(() => {
    const u = this.userSignal();
    if (!u) return false;
    if (u.workspace?.is_operator) return true;
    return u.is_super_admin === true || Number(u.is_super_admin ?? 0) === 1;
  });
  /** Kept name; now means "operator". */
  hasGlobalAdmin = this.isOperator;
  hasSystemAdmin = this.isOperator;

  // ---- Operator Console (super-admin management + permissions) ---------

  /** ROOT operator: implicitly holds every operator permission. */
  isOperatorRoot = computed(() => {
    const u = this.userSignal();
    if (!u || !this.isOperator()) return false;
    return u.is_operator_root === true || Number(u.is_operator_root ?? 0) === 1;
  });

  operatorPermissions = computed<string[]>(() => {
    const u = this.userSignal();
    if (!u || !this.isOperator()) return [];
    if (this.isOperatorRoot()) return OPERATOR_PERMISSION_KEYS.slice();
    return [...new Set([...(u.operator_permissions ?? []), 'workspaces.view'])];
  });

  hasOperatorPermission(key: string): boolean {
    if (!this.isOperator()) return false;
    if (this.isOperatorRoot()) return true;
    return this.operatorPermissions().includes(key);
  }

  /** Active troubleshooting impersonation (real operator identity stashed server-side). */
  isImpersonating = computed(() => !!this.userSignal()?.impersonating);
  impersonation = computed(() => this.userSignal()?.impersonating ?? null);

  /** Operator must set a new password before doing anything else. */
  mustChangePassword = computed(() => {
    const u = this.userSignal();
    return !!u && (u.must_change_password === true || Number(u.must_change_password ?? 0) === 1);
  });

  /** Operator must enable 2-step verification before entering the console. */
  operator2faRequired = computed(() => !!this.userSignal()?.operator_2fa_required);

  changePassword(currentPassword: string, newPassword: string) {
    return this.api
      .post<User>('/auth/change-password', { current_password: currentPassword, new_password: newPassword })
      .pipe(
        tap((res) => {
          if (res.status && res.data) {
            this.userSignal.set(res.data);
            this.persistAuthTokenFromUser(res.data);
          }
        }),
      );
  }

  // ---- workspace RBAC (OWNER / ADMIN / MEMBER / VIEWER) ----------------

  workspaceRole = computed(() => this.userSignal()?.workspace?.role ?? null);

  isWorkspaceAdmin = computed(() => {
    const w = this.userSignal()?.workspace;
    if (w) return !!w.is_workspace_admin;
    return this.isOperator();
  });

  hasWorkspacePermission(key: string): boolean {
    const w = this.userSignal()?.workspace;
    if (!w) return this.isOperator();
    return w.is_operator || w.role === 'OWNER' || (w.permissions ?? []).includes(key);
  }

  /** /admin/** (workspace settings) — OWNER/ADMIN of the current workspace, or operator. */
  canAccessAdminWorkspace = this.isWorkspaceAdmin;

  /** Site role viewer (read-only workspace member). */
  isSiteViewer = computed(() => this.userSignal()?.workspace?.role === 'VIEWER');

  canCreateProject = computed(() => {
    const u = this.userSignal();
    if (!u) return false;
    if (typeof u.can_create_project === 'boolean') return u.can_create_project;
    return this.hasWorkspacePermission('create_project');
  });

  /** Global Projects directory: move to trash / delete / restore. */
  canDeleteProject = computed(() => {
    const u = this.userSignal();
    if (!u) return false;
    if (typeof u.can_delete_project === 'boolean') return u.can_delete_project;
    return this.isWorkspaceAdmin();
  });

  /** Header "+ Create" (issues); viewers need issues/add override from API. */
  canCreateIssues = computed(() => {
    const u = this.userSignal();
    if (!u) return false;
    if (typeof u.can_create_issues === 'boolean') return u.can_create_issues;
    if (this.isSiteViewer()) return false;
    return true;
  });

  /**
   * WizCode operator console (/admin/subscriptions, /admin/plans). Strictly the
   * instance super-admin — NOT workspace admins, NOT legacy role='admin'.
   */
  canAccessSystemAdminRoutes = computed(() => {
    const u = this.userSignal();
    if (u?.workspace?.is_operator) return true;
    if (u?.is_super_admin === true || Number(u?.is_super_admin ?? 0) === 1) return true;
    if (typeof localStorage === 'undefined') return false;
    try {
      const t = localStorage.getItem(WIZTASKS_AUTH_TOKEN_KEY);
      if (!t?.trim()) return false;
      const p = decodeJwtPayload(t.trim());
      const sa = p?.is_super_admin;
      return sa === true || sa === 1 || Number(sa ?? 0) === 1;
    } catch {
      return false;
    }
  });

  isSystemAdmin(): boolean {
    return this.canAccessSystemAdminRoutes();
  }

  constructor(private api: ApiService, private router: Router) {}

  /** Persist signed JWT from login responses (passport for global_role checks). */
  private persistAuthTokenFromUser(user: User | null | undefined): void {
    if (typeof localStorage === 'undefined') return;
    if (user?.auth_token) {
      localStorage.setItem(WIZTASKS_AUTH_TOKEN_KEY, user.auth_token);
    }
  }

  loadUser() {
    return this.api.get<User>('/auth/me').pipe(
      tap((res) => {
        if (res.status && res.data) {
          this.userSignal.set(res.data);
          this.persistAuthTokenFromUser(res.data);
        } else this.userSignal.set(null);
      })
    );
  }

  login(email: string, password: string) {
    return this.api.post<User | Login2FARequired>('/auth/login', { email, password }).pipe(
      tap((res) => {
        if (res.status && res.data) {
          const d = res.data as Login2FARequired | User;
          if ('requires_two_factor' in d && d.requires_two_factor) return;
          const u = d as User;
          this.userSignal.set(u);
          this.persistAuthTokenFromUser(u);
        }
      })
    );
  }

  /** Complete login after 2FA. Returns user on success. */
  verify2fa(pendingToken: string, code: string) {
    return this.api.post<User>('/auth/verify-2fa', { pending_token: pendingToken, code }).pipe(
      tap((res) => {
        if (res.status && res.data) {
          this.userSignal.set(res.data);
          this.persistAuthTokenFromUser(res.data);
        }
      })
    );
  }

  /** Resend email 2FA code for pending login. */
  twofaResendCode(pendingToken: string) {
    return this.api.post<{ message: string }>('/auth/2fa/resend-code', { pending_token: pendingToken });
  }

  /** Start TOTP setup; returns secret and provisioning URI. */
  twofaSetupTotp() {
    return this.api.post<{ secret: string; provisioning_uri: string }>('/auth/2fa/setup-totp', {}).pipe(
      timeout(20000)
    );
  }

  /** Enable 2FA. For totp: pass code; for email: pass password. */
  twofaEnable(method: 'totp' | 'email', code?: string, password?: string) {
    const body: { method: string; code?: string; password?: string } = { method };
    if (method === 'totp' && code) body.code = code;
    if (method === 'email' && password) body.password = password;
    return this.api.post<User>('/auth/2fa/enable', body).pipe(
      timeout(20000),
      tap((res) => {
        if (res.status && res.data) {
          this.userSignal.set(res.data);
          this.persistAuthTokenFromUser(res.data);
        }
      })
    );
  }

  /** Disable 2FA. Requires password confirmation. */
  twofaDisable(password: string) {
    return this.api.post<User>('/auth/2fa/disable', { password }).pipe(
      timeout(20000),
      tap((res) => {
        if (res.status && res.data) {
          this.userSignal.set(res.data);
          this.persistAuthTokenFromUser(res.data);
        }
      })
    );
  }

  /** Sign up with email and password. Provisions a workspace; user must verify + sign in after. */
  register(
    email: string,
    password: string,
    fullName?: string,
    companyName?: string,
    subdomain?: string,
    accountType: 'individual' | 'company' = 'company',
  ) {
    const body: {
      email: string;
      password: string;
      full_name?: string;
      company_name?: string;
      subdomain?: string;
      account_type: 'individual' | 'company';
    } = { email, password, account_type: accountType };
    if (fullName) body.full_name = fullName;
    if (companyName) body.company_name = companyName;
    if (subdomain) body.subdomain = subdomain;
    return this.api.post<{
      id: number;
      email: string;
      full_name: string;
      message: string;
      workspace?: { id: number; name: string; subdomain: string; login_url: string; account_type: string };
    }>('/auth/register', body);
  }

  /** Check whether a workspace address (slug) is available. */
  checkSubdomain(value: string) {
    return this.api.get<{ available: boolean; value: string; suggestion?: string; reason?: string }>(
      '/auth/check-subdomain',
      { value },
    );
  }

  /** Workspaces the signed-in user can enter (path-based tenancy picker). */
  workspaces() {
    return this.api.get<Array<{ id: number; key: string; name: string; slug: string }>>('/auth/workspaces');
  }

  /** Create a new workspace for the signed-in user (they become OWNER). */
  createWorkspace(body: { name: string; subdomain?: string; account_type: 'individual' | 'company' }) {
    return this.api.post<{ workspace: { id: number; name: string; subdomain: string; slug: string } }>(
      '/auth/create-workspace',
      body,
    );
  }

  /** Request password reset link sent to email. */
  forgotPassword(email: string) {
    return this.api.post<{ message: string; dev_reset_link?: string; email_delivery?: string }>(
      '/auth/forgot-password',
      { email },
    );
  }

  /** Set new password using token from reset link. */
  resetPassword(token: string, password: string, passwordConfirm: string) {
    return this.api.post<{ message: string }>('/auth/reset-password', {
      token,
      password,
      password_confirm: passwordConfirm,
    });
  }

  /** Update current user profile (display name, avatar URL, timezone). */
  updateProfile(data: { full_name?: string; avatar_url?: string | null; timezone?: string; notification_retention_days?: number }) {
    return this.api.put<User>('/auth/profile', data).pipe(
      tap((res) => {
        if (res.status && res.data) {
          this.userSignal.set(res.data);
          this.persistAuthTokenFromUser(res.data);
        }
      })
    );
  }

  /** Upload avatar image; returns new avatar_url and updates user signal. */
  uploadAvatar(file: File | Blob, filename = 'avatar.png') {
    const formData = new FormData();
    // If `file` is a Blob (e.g., cropped result), pass a filename explicitly.
    formData.append('avatar', file, filename);
    return this.api.postFormData<{ avatar_url: string }>('/auth/profile/avatar', formData).pipe(
      tap((res) => {
        if (res.status && res.data?.avatar_url) {
          const current = this.userSignal();
          if (current) this.userSignal.set({ ...current, avatar_url: res.data!.avatar_url });
        }
      })
    );
  }

  logout(): void {
    this.api.post('/auth/logout', {}).subscribe();
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(WIZTASKS_AUTH_TOKEN_KEY);
    }
    this.userSignal.set(null);
    this.router.navigate(['/login']);
  }
}
